import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { X, Check } from "lucide-react";
import { useModalFocus } from "../hooks/useModalFocus";
import { SelectInput } from "./fields/SelectInput";
import { COVERAGE_OPTIONS, PLAN_LABELS, YES_NO_OPTIONS } from "../types/employeeConstants";
import type { Employee, YesNo } from "../types/form";

interface EmployeeModalProps {
  mode: "add" | "edit";
  initial?: Employee;
  availablePlans: number;
  offersDental: boolean;
  offersVision: boolean;
  onSave: (employee: Employee) => void;
  onSaveAndAdd?: (employee: Employee) => void;
  onClose: () => void;
}

// uid is assigned by the store when this row is committed via addEmployee/setEmployees.
const emptyEmployee: Employee = {
  firstName: "",
  lastName: "",
  owner: "",
  medicalCoverage: "",
  medicalPlan: "",
  dentalCoverage: "",
  visionCoverage: "",
  cobra: "",
  source: "manual",
};

export function EmployeeModal({
  mode,
  initial,
  availablePlans,
  offersDental,
  offersVision,
  onSave,
  onSaveAndAdd,
  onClose,
}: EmployeeModalProps) {
  const [form, setForm] = useState<Employee>(initial ?? { ...emptyEmployee });
  const [touched, setTouched] = useState(false);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [savedFading, setSavedFading] = useState(false);
  // firstInputRef is used by "Save & Add Another" to land focus on the first
  // field of the fresh form — we deliberately DON'T pass it to useModalFocus,
  // so initial open focuses the container (no mobile keyboard popup).
  const firstInputRef = useRef<HTMLInputElement>(null);
  // scrollRef is the overflow-y-auto body — used to reset scroll to top after
  // "Save & Add Another" so the user sees the fresh form from the heading down,
  // not from wherever the Save buttons left them.
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useModalFocus(onClose);
  const titleId = useId();

  const set = useCallback((field: keyof Employee, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "medicalCoverage" && value !== "Enrolling") {
        next.medicalPlan = "";
      }
      return next;
    });
  }, []);

  const errors: Record<string, string> = {};
  if (touched) {
    if (!form.firstName.trim()) errors.firstName = "First name is required";
    if (!form.lastName.trim()) errors.lastName = "Last name is required";
    if (!form.owner) errors.owner = "Owner is required";
    if (!form.medicalCoverage) errors.medicalCoverage = "Medical coverage is required";
    if (form.medicalCoverage === "Enrolling" && availablePlans > 1 && !form.medicalPlan)
      errors.medicalPlan = "Medical plan is required";
    if (offersDental && !form.dentalCoverage) errors.dentalCoverage = "Dental coverage is required";
    if (offersVision && !form.visionCoverage) errors.visionCoverage = "Vision coverage is required";
    if (!form.cobra) errors.cobra = "COBRA status is required";
  }

  // Boolean() — the && chain returns the last truthy/falsy operand (a string for `.trim()`
  // and the field reads), not a bool. Wrapping keeps `disabled={... && !isValid}` typed.
  const isValid = Boolean(
    Object.keys(errors).length === 0 && form.firstName.trim() && form.lastName.trim()
    && form.owner && form.medicalCoverage && form.cobra
    && (!offersDental || form.dentalCoverage)
    && (!offersVision || form.visionCoverage)
    && (form.medicalCoverage !== "Enrolling" || availablePlans <= 1 || form.medicalPlan),
  );

  const fadeTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => () => fadeTimers.current.forEach(clearTimeout), []);

  const buildEmployee = (source: Employee["source"]): Employee => ({
    ...form,
    medicalPlan: form.medicalCoverage === "Enrolling" ? (availablePlans <= 1 ? "Plan 1" : form.medicalPlan) : "",
    dentalCoverage: offersDental ? form.dentalCoverage : "N/A",
    visionCoverage: offersVision ? form.visionCoverage : "N/A",
    source,
  });

  const handleSave = () => {
    setTouched(true);
    if (!isValid) return;
    onSave(buildEmployee(initial?.source ?? "manual"));
  };

  const handleSaveAndAdd = () => {
    setTouched(true);
    if (!isValid) return;
    const employee = buildEmployee("manual");
    onSaveAndAdd?.(employee);
    setSavedMessage("Employee was saved. Add another below.");
    setSavedFading(false);
    setForm({ ...emptyEmployee });
    setTouched(false);
    scrollRef.current?.scrollTo({ top: 0 });
    firstInputRef.current?.focus();
    fadeTimers.current.forEach(clearTimeout);
    fadeTimers.current = [
      setTimeout(() => setSavedFading(true), 3500),
      setTimeout(() => { setSavedMessage(null); setSavedFading(false); }, 4000),
    ];
  };

  const showMedicalPlan = form.medicalCoverage === "Enrolling" && availablePlans > 1;
  // Clamp to PLAN_LABELS bounds — defensive against bad metadata (e.g. AvailablePlans=99).
  const planCount = Math.max(1, Math.min(PLAN_LABELS.length, availablePlans));
  const plans = PLAN_LABELS.slice(0, planCount);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        ref={containerRef}
        // tabIndex={-1} makes the container programmatically focusable so
        // useModalFocus can land focus inside the dialog on open without
        // putting the container into the tab order.
        tabIndex={-1}
        className="relative bg-white rounded-xl shadow-xl max-w-[90vw] sm:max-w-lg w-full max-h-[70dvh] sm:max-h-[90vh] flex flex-col focus:outline-none"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {/* Save confirmation — floats above the modal, doesn't push form content.
            `dvh` (dynamic viewport height) accounts for mobile browser chrome so
            the 70dvh modal stays within the visible viewport, leaving ~15dvh of
            real room above for this toast to land in. */}
        {savedMessage && (
          <div className={`absolute bottom-full mb-3 left-0 right-0 flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5 shadow-sm overflow-hidden transition-opacity duration-500 ${savedFading ? "opacity-0" : "opacity-100 animate-fade-in"}`} aria-live="polite">
            <Check size={16} className="text-green-600 shrink-0" />
            <span className="text-sm text-green-800 truncate">{savedMessage}</span>
          </div>
        )}

        <div ref={scrollRef} className="p-6 overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 id={titleId} className="text-lg font-semibold text-gray-900">
            {mode === "add" ? "Add Employee" : "Edit Employee"}
          </h3>
          <button
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="emp-firstName" className="block text-sm font-medium text-gray-700 mb-1">
              First Name <span className="text-danger">*</span>
            </label>
            <input
              id="emp-firstName"
              ref={firstInputRef}
              type="text"
              maxLength={40}
              value={form.firstName}
              onChange={(e) => set("firstName", e.target.value)}
              aria-invalid={!!errors.firstName}
              aria-describedby={errors.firstName ? "err-firstName" : undefined}
              className={`w-full px-3 py-2 border rounded-lg text-sm ${errors.firstName ? "border-danger" : "border-gray-300"}`}
            />
            {errors.firstName && <p id="err-firstName" role="alert" className="text-xs text-danger mt-1">{errors.firstName}</p>}
          </div>

          <div>
            <label htmlFor="emp-lastName" className="block text-sm font-medium text-gray-700 mb-1">
              Last Name <span className="text-danger">*</span>
            </label>
            <input
              id="emp-lastName"
              type="text"
              maxLength={80}
              value={form.lastName}
              onChange={(e) => set("lastName", e.target.value)}
              aria-invalid={!!errors.lastName}
              aria-describedby={errors.lastName ? "err-lastName" : undefined}
              className={`w-full px-3 py-2 border rounded-lg text-sm ${errors.lastName ? "border-danger" : "border-gray-300"}`}
            />
            {errors.lastName && <p id="err-lastName" role="alert" className="text-xs text-danger mt-1">{errors.lastName}</p>}
          </div>

          <ToggleField
            label="Owner"
            value={form.owner}
            onChange={(v) => set("owner", v)}
            error={errors.owner}
            required
          />

          <SelectInput
            label="Medical Coverage"
            value={form.medicalCoverage}
            onChange={(v) => set("medicalCoverage", v)}
            options={COVERAGE_OPTIONS}
            error={errors.medicalCoverage}
            required
          />

          {showMedicalPlan && (
            <div className="animate-fade-in">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medical Plan <span className="text-danger">*</span>
              </label>
              <RadioGroup
                ariaLabel="Medical Plan"
                options={plans}
                value={form.medicalPlan}
                onChange={(v) => set("medicalPlan", v)}
              />
              {errors.medicalPlan && <p role="alert" className="text-xs text-danger mt-1">{errors.medicalPlan}</p>}
            </div>
          )}

          {offersDental && (
            <SelectInput
              label="Dental Coverage"
              value={form.dentalCoverage}
              onChange={(v) => set("dentalCoverage", v)}
              options={COVERAGE_OPTIONS}
              error={errors.dentalCoverage}
              required
            />
          )}

          {offersVision && (
            <SelectInput
              label="Vision Coverage"
              value={form.visionCoverage}
              onChange={(v) => set("visionCoverage", v)}
              options={COVERAGE_OPTIONS}
              error={errors.visionCoverage}
              required
            />
          )}

          <ToggleField
            label="On COBRA or in COBRA Election Period"
            value={form.cobra}
            onChange={(v) => set("cobra", v)}
            error={errors.cobra}
            required
          />
        </div>

        <div className={`grid gap-3 mt-6 pt-4 border-t border-brand-green-light ${mode === "add" && onSaveAndAdd ? "grid-cols-3" : "grid-cols-2"}`}>
          <button
            onClick={onClose}
            className="py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer min-h-[44px]"
          >
            Cancel
          </button>
          {mode === "add" && onSaveAndAdd && (
            <button
              onClick={handleSaveAndAdd}
              disabled={touched && !isValid}
              className="px-3 py-2 text-sm font-medium text-brand-green border border-brand-green rounded-lg hover:bg-brand-green-05 cursor-pointer min-h-[44px] disabled:opacity-40 disabled:cursor-default"
            >
              Save & Add Another
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={touched && !isValid}
            className="py-2 text-sm font-medium text-white bg-brand-green rounded-lg hover:bg-brand-green-dark cursor-pointer min-h-[44px] disabled:opacity-40 disabled:cursor-default"
          >
            {mode === "add" ? "Add Employee" : "Save Changes"}
          </button>
        </div>
        </div>

      </div>
    </div>
  );
}

function ToggleField({
  label,
  value,
  onChange,
  error,
  required,
}: {
  label: string;
  value: YesNo;
  onChange: (v: YesNo) => void;
  error?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}{required && <span className="text-danger">&nbsp;*</span>}
      </label>
      <RadioGroup
        ariaLabel={label}
        options={YES_NO_OPTIONS}
        value={value}
        onChange={(v) => onChange(v as YesNo)}
      />
      {error && <p role="alert" className="text-xs text-danger mt-1">{error}</p>}
    </div>
  );
}

/**
 * WCAG-compliant radiogroup: arrow keys move focus + selection between options,
 * Home/End jump to ends, and only the selected option (or first if none) is in
 * the tab order (roving tabindex). Click still works for mouse users.
 */
function RadioGroup({
  ariaLabel,
  options,
  value,
  onChange,
}: {
  ariaLabel: string;
  options: readonly string[];
  value: string;
  onChange: (v: string) => void;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedIdx = options.indexOf(value);
  // First option is in tab order when nothing is selected, so the group is reachable.
  const tabIdx = selectedIdx === -1 ? 0 : selectedIdx;

  const handleKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    let next: number | null = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (i + 1) % options.length;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (i - 1 + options.length) % options.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = options.length - 1;
    if (next === null) return;
    e.preventDefault();
    onChange(options[next]);
    refs.current[next]?.focus();
  };

  return (
    <div className="flex gap-2" role="radiogroup" aria-label={ariaLabel}>
      {options.map((opt, i) => (
        <button
          key={opt}
          ref={(el) => { refs.current[i] = el; }}
          type="button"
          role="radio"
          aria-checked={value === opt}
          tabIndex={i === tabIdx ? 0 : -1}
          onClick={() => onChange(opt)}
          onKeyDown={(e) => handleKey(e, i)}
          className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border cursor-pointer transition-colors ${
            value === opt
              ? "bg-brand-green text-white border-brand-green"
              : "bg-white text-gray-700 border-gray-300 hover:border-brand-green"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}
